"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuthStore } from "@/store/auth.store";
import { useAllCarts } from "@/hooks/use-cart";
import { House, ShoppingBag, CircleUser } from "lucide-react";
import { loginHref } from "@/lib/next-path";

const NAV_ITEMS = [
  {
    href: "/home",
    label: "Accueil",
    icon: <House className="h-6 w-6" />,
    requiresAuth: false,
    showCartBadge: false,
  },
  {
    href: "/cart",
    label: "Paniers",
    icon: <ShoppingBag className="h-6 w-6" />,
    requiresAuth: true,
    showCartBadge: true,
  },
  {
    href: "/account",
    label: "Compte",
    icon: <CircleUser className="h-6 w-6" />,
    requiresAuth: false,
    showCartBadge: false,
  },
] as const;

export function BottomNav() {
  const pathname = usePathname();
  const user = useAuthStore((s) => s.user);
  const { data: allCarts } = useAllCarts();
  const cartCount = allCarts?.length ?? 0;

  // Store pages are a full-screen experience on mobile (own header + close button).
  if (pathname.startsWith("/stores/")) return null;

  return (
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-gray-200 dark:border-[#313131] bg-white dark:bg-[#1E1E1E] safe-area-bottom">
      <div className="flex items-stretch h-16">
        {NAV_ITEMS.map((item) => {
          // Se connecter depuis la barre du bas doit ramener ici, pas sur /home.
          const href = !user && item.requiresAuth ? loginHref(pathname) : item.href;
          const isActive = pathname === item.href || pathname.startsWith(item.href + "/");
          const showBadge = item.showCartBadge && cartCount > 0;
          return (
            <Link
              key={item.href}
              href={href}
              className={`flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${
                isActive
                  ? "text-primary"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              <span className={`relative ${isActive ? "text-primary" : ""}`}>
                {item.icon}
                {showBadge && (
                  <span className="absolute -top-1 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white leading-none">
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </span>
              {item.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
